import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, TrendingUp, Minus, Mail, Loader2 } from 'lucide-react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useProject } from '@/lib/ProjectContext';
import api, { reportAPI } from '@/lib/api';
import { useT } from '@/lib/LanguageContext';
import { getCurrentUser } from '@/lib/auth';
import { toast } from 'sonner';
import { safeFormat } from '@/lib/utils';

const ACTIVITY_COLORS = {
  email: '#4C6B82',
  call: '#A62B1F',
  linkedin: '#A62B1F',
  meeting: '#10B981',
  note: '#F59E0B'
};

const STAGE_COLORS = {
  new_lead: '#9AA3AB',
  contacted: '#4C6B82',
  engaged: '#A62B1F',
  demo_scheduled: '#F59E0B',
  proposal_sent: '#EC4899',
  won: '#10B981',
  lost: '#5C6670'
};

const STAGE_NAMES = {
  new_lead: 'New Lead',
  contacted: 'Contacted',
  engaged: 'Engaged',
  demo_scheduled: 'Demo Scheduled',
  proposal_sent: 'Proposal Sent',
  won: 'Won',
  lost: 'Lost'
};

// Calendar periods run from the 1st (or the quarter's first day); the rolling ones count back from today.
const REPORT_PERIODS = [
  ['this_month', 'This month'], ['last_month', 'Last month'], ['this_quarter', 'This quarter'], ['last_quarter', 'Last quarter'],
  ['ytd', 'Year to date'], ['pick', 'Pick a month…'], ['7d', 'Last 7 days'], ['30d', 'Last 30 days'], ['90d', 'Last 90 days'], ['all', 'All time'],
];
const thisMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const periodName = (p) => {
  if (/^\d{4}-\d{2}$/.test(p)) { const [y, m] = p.split('-').map(Number); return new Date(y, m - 1, 1).toLocaleDateString('en-CA', { month: 'long', year: 'numeric' }); }
  return (REPORT_PERIODS.find(([k]) => k === p) || [p, p])[1];
};

export default function Reporting() {
  const { activeProject, projects } = useProject();
  const currentUser = getCurrentUser();
  const t = useT();
  const [period, setPeriod] = useState('this_month');
  // scope is either 'all' or a project _id
  const [scope, setScope] = useState('all');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  // Email report dialog
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTo, setEmailTo] = useState(currentUser?.email || '');
  const [sending, setSending] = useState(false);

  const selectedProject = scope !== 'all' ? projects.find(p => p._id === scope) : null;
  const scopeLabel = scope === 'all'
    ? `All Projects${report?.project_count ? ` (${report.project_count})` : ''}`
    : (selectedProject?.name || '—');

  const handleEmailReport = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      if (scope === 'all') {
        await reportAPI.emailAggregate(emailTo, period);
      } else {
        await reportAPI.emailProject(scope, emailTo, period);
      }
      toast.success(`Report sent to ${emailTo}`);
      setEmailDialogOpen(false);
    } catch (err) {
      toast.error('Failed to send report', { description: err.response?.data?.detail || err.message });
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [period, scope]);

  const loadReport = async () => {
    try {
      setLoading(true);
      const url =
        scope === 'all'
          ? `/api/projects/aggregate/report`
          : `/api/projects/${scope}/report`;
      const response = await api.get(url, {
        params:
          scope === 'all'
            ? { period, project_id: null }
            : { period },
      });
      setReport(response.data);
    } catch (error) {
      console.error('Error loading report:', error);
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (!report) return;

    const scopeLabel = scope === 'all' ? 'All Projects' : (activeProject?.name || 'Project');
    const rows = [
      ['Stagehead OS Report'],
      ['Scope', scopeLabel],
      ['Period', periodName(period)],
      ['Generated', safeFormat(new Date(), 'MMM d, yyyy h:mm a')],
      [],
      ['OVERVIEW'],
      ['Contacts Added', report.contacts_added],
      ['Emails Sent', report.emails_sent],
      ['Reply Rate', `${report.reply_rate}%`],
      ['Meetings Completed', (report.meetings || {}).completed ?? 0],
      ['Deals Won', report.won],
      ['Win Rate', `${report.win_rate}%`],
      [],
      ['PIPELINE'],
      ...Object.entries(report.pipeline).map(([stage, count]) => [STAGE_NAMES[stage] || stage, count]),
      [],
      ['ACTIVITIES'],
      ...Object.entries(report.activities).map(([type, count]) => [type.charAt(0).toUpperCase() + type.slice(1), count]),
      [],
      ['STAFF LEADERBOARD'],
      ['Staff', 'Emails', 'Calls', 'LinkedIn', 'Meetings', 'Wins', 'Total'],
      ...(report.staff_leaderboard || []).map(staff => [
        staff._id,
        staff.emails,
        staff.calls,
        staff.linkedin,
        staff.meetings,
        staff.wins,
        staff.total
      ])
    ];

    const csv = rows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const slug = (scope === 'all' ? 'all-projects' : (activeProject?.name || 'project'))
      .toLowerCase()
      .replace(/ /g, '-');
    a.download = `stagehead-report-${slug}-${period}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported');
  };

  if (loading || !report) {
    return (
      <Layout title={t('reporting.title')}>
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <p className="text-[#5C6670]" style={{ fontFamily: 'Manrope' }}>
            {t('reporting.loading')}
          </p>
        </div>
      </Layout>
    );
  }

  const pipelineData = Object.entries(report.pipeline).map(([stage, count]) => ({
    name: STAGE_NAMES[stage] || stage,
    value: count,
    fill: STAGE_COLORS[stage] || '#9AA3AB'
  }));

  const wonLostData = [
    { name: 'Won', value: report.won, fill: '#10B981' },
    { name: 'Lost', value: report.lost, fill: '#5C6670' }
  ];

  const activityData = Object.entries(report.activities).map(([type, count]) => ({
    name: type.charAt(0).toUpperCase() + type.slice(1),
    value: count,
    fill: ACTIVITY_COLORS[type]
  }));

  const safeMeetings = report.meetings || { scheduled: 0, completed: 0, no_show: 0, show_rate: 0 };
  const meetingsData = [
    { name: 'Completed', value: safeMeetings.completed, fill: '#10B981' },
    { name: 'Scheduled', value: safeMeetings.scheduled, fill: '#4C6B82' },
    { name: 'No Show', value: safeMeetings.no_show, fill: '#5C6670' }
  ];

  const staffLeaderboard = Array.isArray(report.staff_leaderboard) ? report.staff_leaderboard : [];
  const maxLeaderboardTotal = Math.max(...staffLeaderboard.map(s => s.total || 0), 1);
  const reportSequences = report.sequences || { active: 0, completed: 0 };
  const reportSocial = report.social || { total: 0, by_platform: { linkedin: 0, facebook: 0, instagram: 0 } };
  const reportMeetings = report.meetings || { scheduled: 0, completed: 0, no_show: 0, show_rate: 0 };

  return (
    <Layout title={t('reporting.title')}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div
              className="w-4 h-4 rounded-full"
              style={{
                backgroundColor: selectedProject?.color || '#A62B1F',
              }}
            />
            <div>
              <h1 className="text-2xl font-bold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                {t('reporting.title')}
              </h1>
              <p className="text-sm text-[#5C6670]">{scopeLabel}</p>
            </div>
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            {/* Project selector */}
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="w-[200px]" data-testid="report-scope-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map(p => (
                  <SelectItem key={p._id} value={p._id}>
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: p.color || '#A62B1F' }} />
                      {p.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Period */}
            <select
              className="h-9 rounded-md border border-input px-2 text-sm bg-white"
              value={/^\d{4}-\d{2}$/.test(period) ? 'pick' : period}
              onChange={(e) => setPeriod(e.target.value === 'pick' ? thisMonth() : e.target.value)}
              data-testid="report-period"
            >
              {REPORT_PERIODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            {/^\d{4}-\d{2}$/.test(period) && (
              <Input type="month" className="h-9 w-44" value={period} max={thisMonth()} onChange={(e) => e.target.value && setPeriod(e.target.value)} />
            )}

            {/* Export Button */}
            <Button
              variant="outline"
              onClick={handleExportCSV}
              data-testid="export-csv-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>

            {/* Email PDF Button */}
            <Button
              variant="outline"
              onClick={() => { setEmailTo(currentUser?.email || ''); setEmailDialogOpen(true); }}
            >
              <Mail className="w-4 h-4 mr-2" />
              Email PDF
            </Button>
          </div>
        </div>

        {/* Row 1 - Stat Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatCard
            label="Contacts Added"
            value={report.contacts_added}
          />
          <StatCard
            label="Emails Sent"
            value={report.emails_sent}
          />
          <StatCard
            label="Reply Rate"
            value={`${report.reply_rate}%`}
            trend={report.reply_rate > 20 ? 'up' : null}
          />
          <StatCard
            label="Meetings Completed"
            value={safeMeetings.completed}
          />
          <StatCard
            label="Deals Won"
            value={report.won}
          />
          <StatCard
            label="Win Rate"
            value={`${report.win_rate}%`}
            trend={report.win_rate > 20 ? 'up' : null}
          />
        </div>

        {/* Row 2 - Pipeline & Won/Lost */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Pipeline Funnel */}
          <Card className="lg:col-span-3">
            <CardHeader>
              <CardTitle className="text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                Pipeline Funnel
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={pipelineData} layout="vertical" margin={{ left: 100 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="name" width={100} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#8884d8">
                    {pipelineData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Won vs Lost */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                Won vs Lost
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={wonLostData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label
                  >
                    {wonLostData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
              <div className="text-center mt-4">
                <p className="text-3xl font-bold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                  {report.win_rate}%
                </p>
                <p className="text-sm text-[#5C6670]">{t('reporting.win_rate')}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Row 3 - Activities & Email Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Activities Chart */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                Activity Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={activityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#8884d8">
                    {activityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Email Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                Email Performance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-[#5C6670]">Sent</span>
                  <span className="font-bold text-[#1E2A33]">{report.emails_sent}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-[#5C6670]">Received</span>
                  <span className="font-bold text-[#1E2A33]">{report.emails_received}</span>
                </div>
                <div className="relative h-6 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="absolute h-full bg-[#4C6B82] rounded-full"
                    style={{ width: `${Math.min(report.reply_rate, 100)}%` }}
                  />
                </div>
              </div>

              <div className="bg-[#F3EFE7] p-4 rounded-lg">
                <p className="text-3xl font-bold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                  {report.reply_rate}%
                </p>
                <p className="text-sm text-[#5C6670] mt-1">Reply Rate</p>
                <p className="text-xs text-[#5C6670] mt-2">
                  {report.emails_sent > 0
                    ? `1 in ${Math.round(report.emails_sent / Math.max(report.emails_received, 1))} emails gets a reply`
                    : 'No emails sent yet'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Row 4 - Staff Leaderboard */}
        <Card>
          <CardHeader>
            <CardTitle className="text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
              Staff Leaderboard
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Staff</TableHead>
                  <TableHead className="text-center">Emails</TableHead>
                  <TableHead className="text-center">Calls</TableHead>
                  <TableHead className="text-center">LinkedIn</TableHead>
                  <TableHead className="text-center">Meetings</TableHead>
                  <TableHead className="text-center">Wins</TableHead>
                  <TableHead className="text-center">Total</TableHead>
                  <TableHead>Activity</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {staffLeaderboard.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-[#5C6670]">
                      No staff activity data available yet
                    </TableCell>
                  </TableRow>
                )}
                {staffLeaderboard.map((staff, index) => {
                  const staffName = staff._id.split('@')[0];
                  const capitalizedName = staffName.charAt(0).toUpperCase() + staffName.slice(1);
                  const activityWidth = (staff.total / maxLeaderboardTotal) * 100;

                  return (
                    <TableRow
                      key={staff._id}
                      className={index === 0 ? 'border-l-4 border-[#A62B1F]' : ''}
                      data-testid={`staff-${staff._id}`}
                    >
                      <TableCell className="font-semibold text-[#1E2A33]">
                        {capitalizedName}
                      </TableCell>
                      <TableCell className="text-center">{staff.emails}</TableCell>
                      <TableCell className="text-center">{staff.calls}</TableCell>
                      <TableCell className="text-center">{staff.linkedin}</TableCell>
                      <TableCell className="text-center">{staff.meetings}</TableCell>
                      <TableCell className="text-center">{staff.wins}</TableCell>
                      <TableCell className="text-center font-bold">{staff.total}</TableCell>
                      <TableCell>
                        <div className="w-full h-6 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#A62B1F] rounded-full"
                            style={{ width: `${activityWidth}%` }}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Row 5 - Meetings, Sequences, Social */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Meetings Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                Meetings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={meetingsData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {meetingsData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-4">
                {meetingsData.map((meeting) => (
                  <div key={meeting.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: meeting.fill }}
                      />
                      <span className="text-sm text-[#5C6670]">{meeting.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-[#1E2A33]">{meeting.value}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-[#F3EFE7] rounded-lg">
                <p className="text-sm text-[#5C6670]">Show Rate</p>
                <p className="text-2xl font-bold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                  {safeMeetings.show_rate}%
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Campaigns Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                Campaigns
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-[#5C6670]">Active Enrollments</span>
                  <span className="font-bold text-[#1E2A33]">{reportSequences.active}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#5C6670]">Completed</span>
                  <span className="font-bold text-[#1E2A33]">{reportSequences.completed}</span>
                </div>
              </div>

              <div>
                <p className="text-sm text-[#5C6670] mb-2">Completion Rate</p>
                <div className="relative h-6 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="absolute h-full bg-[#A62B1F] rounded-full"
                    style={{
                      width: `${Math.min(
                        (reportSequences.completed /
                          Math.max(reportSequences.active + reportSequences.completed, 1)) *
                          100,
                        100
                      )}%`
                    }}
                  />
                </div>
                <p className="text-xs text-[#5C6670] mt-2">
                  {reportSequences.completed > 0
                    ? `${Math.round(
                        (reportSequences.completed /
                          (reportSequences.active + reportSequences.completed)) *
                          100
                      )}% completed`
                    : 'No completed sequences yet'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Social Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                Social Media
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-[#5C6670]">Total Published</p>
                <p className="text-3xl font-bold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                  {reportSocial.total}
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#0A66C2]" />
                    <span className="text-sm text-[#5C6670]">LinkedIn</span>
                  </div>
                  <span className="font-semibold text-[#1E2A33]">
                    {reportSocial.by_platform?.linkedin ?? 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#1877F2]" />
                    <span className="text-sm text-[#5C6670]">Facebook</span>
                  </div>
                  <span className="font-semibold text-[#1E2A33]">
                    {reportSocial.by_platform?.facebook ?? 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#E4405F]" />
                    <span className="text-sm text-[#5C6670]">Instagram</span>
                  </div>
                  <span className="font-semibold text-[#1E2A33]">
                    {reportSocial.by_platform?.instagram ?? 0}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Email Report Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Email Report as PDF</DialogTitle>
            <DialogDescription>
              A PDF of the current report ({scopeLabel}, {periodName(period)}) will be sent to the address below.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEmailReport} className="space-y-4">
            <div>
              <Label htmlFor="report-email">Send to</Label>
              <Input
                id="report-email"
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                required
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEmailDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={sending}
                style={{ backgroundColor: '#A62B1F' }}
                className="text-white"
              >
                {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</> : <><Mail className="w-4 h-4 mr-2" />Send Report</>}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

// Stat Card Component
function StatCard({ label, value, trend }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-[#5C6670]" style={{ fontFamily: 'Manrope' }}>
              {label}
            </p>
            <p className="text-3xl font-bold text-[#1E2A33] mt-2" style={{ fontFamily: 'Manrope' }}>
              {value}
            </p>
          </div>
          {trend === 'up' && (
            <TrendingUp className="w-6 h-6 text-[#A62B1F]" />
          )}
          {trend === 'down' && (
            <Minus className="w-6 h-6 text-[#5C6670]" />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
